import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocuments } from '../../hooks';
import { Button, Card, CardBody, Spinner, Alert, VerificationBadge, ModalPortal } from '../../components';
import { formatDate, formatFileSize } from '../../utils';
import { Document, DocumentType } from '../../types';

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  certificate: 'Certificate',
  license: 'License',
  id_document: 'ID Document',
  police_clearance: 'Police Clearance',
  proof_of_address: 'Proof of Address',
  reference_letter: 'Reference Letter',
  other: 'Other',
};

const DOCUMENT_TYPE_ICONS: Record<DocumentType, string> = {
  certificate: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
  license: 'M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2',
  id_document: 'M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0',
  police_clearance: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  proof_of_address: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  reference_letter: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  other: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z',
};

const isImage = (mimeType?: string, fileName?: string) => {
  if (mimeType) return mimeType.startsWith('image/');
  if (fileName) return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
  return false;
};

const isPdf = (mimeType?: string, fileName?: string) => {
  if (mimeType) return mimeType === 'application/pdf';
  if (fileName) return /\.pdf$/i.test(fileName);
  return false;
};

interface DocumentViewerProps {
  document: Document;
  onClose: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ document, onClose }) => {
  return (
    <ModalPortal>
    <div className="fixed inset-0 bg-black bg-opacity-75 z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 break-all">{document.file_name}</h3>
            <p className="text-sm text-gray-500">{DOCUMENT_TYPE_LABELS[document.document_type]}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={document.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span className="hidden sm:inline">Open in new tab</span>
              <span className="sm:hidden">Open</span>
            </a>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50 min-h-64">
          {isImage(document.mime_type, document.file_name) ? (
            <img
              src={document.file_url}
              alt={document.file_name}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow"
            />
          ) : isPdf(document.mime_type, document.file_name) ? (
            <iframe
              src={document.file_url}
              className="w-full h-[70vh] rounded border"
              title={document.file_name}
            />
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500 mb-4">Preview not available for this file type</p>
              <a
                href={document.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700"
              >
                Download File
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

export const Documents: React.FC = () => {
  const { documents, loading, error, deleteDocument, clearError } = useDocuments();
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this document?')) return;
    setDeletingId(id);
    await deleteDocument(id);
    setDeletingId(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Group documents by type
  const grouped = documents.reduce<Record<string, Document[]>>((acc, doc) => {
    const type = doc.document_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {});

  return (
    <>
      {selectedDoc && (
        <DocumentViewer document={selectedDoc} onClose={() => setSelectedDoc(null)} />
      )}

      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold">Documents</h1>
            <p className="text-sm text-gray-500 mt-1">{documents.length} document{documents.length !== 1 ? 's' : ''} uploaded</p>
          </div>
          <Link to="/documents/upload" className="self-start shrink-0">
            <Button>Upload Document</Button>
          </Link>
        </div>

        {error && (
          <Alert variant="danger" onClose={clearError}>{error}</Alert>
        )}

        {documents.length > 0 ? (
          <div className="space-y-6">
            {Object.entries(grouped).map(([type, docs]) => (
              <div key={type}>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  {DOCUMENT_TYPE_LABELS[type as DocumentType]}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {docs.map((doc) => (
                    <Card key={doc.id}>
                      <CardBody>
                        <div className="flex items-start gap-3">
                          {/* Thumbnail or icon */}
                          <div
                            className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-gray-200 overflow-hidden"
                            onClick={() => setSelectedDoc(doc)}
                          >
                            {isImage(doc.mime_type, doc.file_name) ? (
                              <img
                                src={doc.file_url}
                                alt={doc.file_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <svg
                                className={`w-7 h-7 ${isPdf(doc.mime_type, doc.file_name) ? 'text-red-400' : 'text-gray-400'}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={DOCUMENT_TYPE_ICONS[doc.document_type]} />
                              </svg>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <button
                              className="text-sm font-medium text-gray-900 hover:text-blue-600 text-left break-all w-full"
                              onClick={() => setSelectedDoc(doc)}
                            >
                              {doc.file_name}
                            </button>
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-xs text-gray-400">
                                {formatDate(doc.created_at)}
                              </p>
                              {doc.file_size && (
                                <span className="text-xs text-gray-400">· {formatFileSize(doc.file_size)}</span>
                              )}
                            </div>
                            {(doc.document_type === 'id_document' || doc.document_type === 'police_clearance') && (
                              <div className="mt-1">
                                <VerificationBadge status={doc.verification_status} />
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              onClick={() => setSelectedDoc(doc)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="View"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(doc.id)}
                              disabled={deletingId === doc.id}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardBody>
              <div className="text-center py-12">
                <svg className="mx-auto w-14 h-14 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-500 font-medium">No documents uploaded yet</p>
                <p className="text-sm text-gray-400 mt-1">Upload certificates, ID documents, and more</p>
                <Link to="/documents/upload" className="inline-block mt-4">
                  <Button>Upload Your First Document</Button>
                </Link>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </>
  );
};
